import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _classCallCheck from "/drone/src/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/drone/src/node_modules/@babel/runtime/helpers/esm/createClass.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { uuid4, timestampInSeconds, logger, dropUndefinedKeys } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { getMetricSummaryJsonForSpan } from '../metrics/metric-summary.js';
import { SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_PROFILE_ID } from '../semanticAttributes.js';
import { getRootSpan } from '../utils/getRootSpan.js';
import { TRACE_FLAG_SAMPLED, TRACE_FLAG_NONE, spanToJSON, spanTimeInputToSeconds, spanToTraceHeader, spanToTraceContext } from '../utils/spanUtils.js';
import { setHttpStatus as _setHttpStatus } from './spanstatus.js';

/**
 * Keeps track of finished spans for a given transaction
 * @internal
 * @hideconstructor
 * @hidden
 */
var SpanRecorder = /*#__PURE__*/function () {
  function SpanRecorder() {
    var maxlen = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1000;
    _classCallCheck(this, SpanRecorder);
    this._maxlen = maxlen;
    this.spans = [];
  }

  /**
   * This is just so that we don't run out of memory while recording a lot
   * of spans. At some point we just stop and flush out the start of the
   * trace tree (i.e.the first n spans with the smallest
   * start_timestamp).
   */
  _createClass(SpanRecorder, [{
    key: "add",
    value: function add(span) {
      if (this.spans.length > this._maxlen) {
        // eslint-disable-next-line deprecation/deprecation
        span.spanRecorder = undefined;
      } else {
        this.spans.push(span);
      }
    }
  }]);
  return SpanRecorder;
}();
/**
 * Span contains all data about a span
 */
var Span = /*#__PURE__*/function () {
  /**
   * Tags for the span.
   * @deprecated Use `spanToJSON(span).atttributes` instead.
   */

  /**
   * Data for the span.
   * @deprecated Use `spanToJSON(span).atttributes` instead.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  /**
   * List of spans that were finalized
   *
   * @deprecated This property will no longer be public. Span recording will be handled internally.
   */

  /**
   * @inheritDoc
   * @deprecated Use top level `Sentry.getRootSpan()` instead
   */

  /**
   * The instrumenter that created this span.
   *
   * TODO (v8): This can probably be replaced by an `instanceOf` check of the span class.
   *            the instrumenter can only be sentry or otel so we can check the span instance
   *            to verify which one it is and remove this field entirely.
   *
   * @deprecated This field will be removed.
   */

  /** Epoch timestamp in seconds when the span started. */

  /** Epoch timestamp in seconds when the span ended. */

  /** Internal keeper of the status */

  /**
   * You should never call the constructor manually, always use `Sentry.startTransaction()`
   * or call `startChild()` on an existing span.
   * @internal
   * @hideconstructor
   * @hidden
   */
  function Span() {
    var _objectSpread2;
    var spanContext = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Span);
    this._traceId = spanContext.traceId || uuid4();
    this._spanId = spanContext.spanId || uuid4().substring(16);
    this._startTime = spanContext.startTimestamp || timestampInSeconds();
    // eslint-disable-next-line deprecation/deprecation
    this.tags = spanContext.tags ? _objectSpread({}, spanContext.tags) : {};
    // eslint-disable-next-line deprecation/deprecation
    this.data = spanContext.data ? _objectSpread({}, spanContext.data) : {};
    // eslint-disable-next-line deprecation/deprecation
    this.instrumenter = spanContext.instrumenter || 'sentry';
    this._attributes = {};
    this.setAttributes(_objectSpread((_objectSpread2 = {}, _defineProperty(_objectSpread2, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, spanContext.origin || 'manual'), _defineProperty(_objectSpread2, SEMANTIC_ATTRIBUTE_SENTRY_OP, spanContext.op), _objectSpread2), spanContext.attributes));

    // eslint-disable-next-line deprecation/deprecation
    this._name = spanContext.name || spanContext.description;
    if (spanContext.parentSpanId) {
      this._parentSpanId = spanContext.parentSpanId;
    }
    // We want to include booleans as well here
    if ('sampled' in spanContext) {
      this._sampled = spanContext.sampled;
    }
    if (spanContext.status) {
      this._status = spanContext.status;
    }
    if (spanContext.endTimestamp) {
      this._endTime = spanContext.endTimestamp;
    }
    if (spanContext.exclusiveTime !== undefined) {
      this._exclusiveTime = spanContext.exclusiveTime;
    }
    this._measurements = spanContext.measurements ? _objectSpread({}, spanContext.measurements) : {};
  }

  // This rule conflicts with another eslint rule :(
  /* eslint-disable @typescript-eslint/member-ordering */

  /**
   * An alias for `description` of the Span.
   * @deprecated Use `spanToJSON(span).description` instead.
   */
  _createClass(Span, [{
    key: "name",
    get: function get() {
      return this._name || '';
    }

    /**
     * Update the name of the span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */,
    set: function set(name) {
      this.updateName(name);
    }

    /**
     * Get the description of the Span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */
  }, {
    key: "description",
    get: function get() {
      return this._name;
    }

    /**
     * Get the description of the Span.
     * @deprecated Use `spanToJSON(span).description` instead.
     */,
    set: function set(description) {
      this._name = description;
    }

    /**
     * The ID of the trace.
     * @deprecated Use `spanContext().traceId` instead.
     */
  }, {
    key: "traceId",
    get: function get() {
      return this._traceId;
    }

    /**
     * The ID of the trace.
     * @deprecated You cannot update the traceId of a span after span creation.
     */,
    set: function set(traceId) {
      this._traceId = traceId;
    }

    /**
     * The ID of the span.
     * @deprecated Use `spanContext().spanId` instead.
     */
  }, {
    key: "spanId",
    get: function get() {
      return this._spanId;
    }

    /**
     * The ID of the span.
     * @deprecated You cannot update the spanId of a span after span creation.
     */,
    set: function set(spanId) {
      this._spanId = spanId;
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `startSpan` functions instead.
     */
  }, {
    key: "parentSpanId",
    get:
    /**
     * @inheritDoc
     *
     * @deprecated Use `spanToJSON(span).parent_span_id` instead.
     */
    function get() {
      return this._parentSpanId;
    }

    /**
     * Was this span chosen to be sent as part of the sample?
     * @deprecated Use `isRecording()` instead.
     */,
    set: function set(string) {
      this._parentSpanId = string;
    }
  }, {
    key: "sampled",
    get: function get() {
      return this._sampled;
    }

    /**
     * Was this span chosen to be sent as part of the sample?
     * @deprecated You cannot update the sampling decision of a span after span creation.
     */,
    set: function set(sampled) {
      this._sampled = sampled;
    }

    /**
     * Attributes for the span.
     * @deprecated Use `spanToJSON(span).atttributes` instead.
     */
  }, {
    key: "attributes",
    get: function get() {
      return this._attributes;
    }

    /**
     * Attributes for the span.
     * @deprecated Use `setAttributes()` instead.
     */,
    set: function set(attributes) {
      this._attributes = attributes;
    }

    /**
     * Timestamp in seconds (epoch time) indicating when the span started.
     * @deprecated Use `spanToJSON()` instead.
     */
  }, {
    key: "startTimestamp",
    get: function get() {
      return this._startTime;
    }

    /**
     * Timestamp in seconds (epoch time) indicating when the span started.
     * @deprecated In v8, you will not be able to update the span start time after creation.
     */,
    set: function set(startTime) {
      this._startTime = startTime;
    }

    /**
     * Timestamp in seconds when the span ended.
     * @deprecated Use `spanToJSON()` instead.
     */
  }, {
    key: "endTimestamp",
    get: function get() {
      return this._endTime;
    }

    /**
     * Timestamp in seconds when the span ended.
     * @deprecated Set the end time via `span.end()` instead.
     */,
    set: function set(endTime) {
      this._endTime = endTime;
    }

    /**
     * The status of the span.
     *
     * @deprecated Use `spanToJSON().status` instead to get the status.
     */
  }, {
    key: "status",
    get: function get() {
      return this._status;
    }

    /**
     * The status of the span.
     *
     * @deprecated Use `.setStatus()` instead to set or update the status.
     */,
    set: function set(status) {
      this._status = status;
    }

    /**
     * Operation of the span
     *
     * @deprecated Use `spanToJSON().op` to read the op instead.
     */
  }, {
    key: "op",
    get: function get() {
      return this._attributes[SEMANTIC_ATTRIBUTE_SENTRY_OP];
    }

    /**
     * Operation of the span
     *
     * @deprecated Use `startSpan()` functions to set or `span.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_OP, 'op')
     *             to update the span instead.
     */,
    set: function set(op) {
      this.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_OP, op);
    }

    /**
     * The origin of the span, giving context about what created the span.
     *
     * @deprecated Use `spanToJSON().origin` to read the origin instead.
     */
  }, {
    key: "origin",
    get: function get() {
      return this._attributes[SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN];
    }

    /**
     * The origin of the span, giving context about what created the span.
     *
     * @deprecated Use `startSpan()` functions to set the origin instead.
     */,
    set: function set(origin) {
      this.setAttribute(SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, origin);
    }

    /* eslint-enable @typescript-eslint/member-ordering */

    /** @inheritdoc */
  }, {
    key: "spanContext",
    value: function spanContext() {
      var spanId = this._spanId,
        traceId = this._traceId,
        sampled = this._sampled;
      return {
        spanId: spanId,
        traceId: traceId,
        traceFlags: sampled ? TRACE_FLAG_SAMPLED : TRACE_FLAG_NONE
      };
    }

    /**
     * Creates a new `Span` while setting the current `Span.id` as `parentSpanId`.
     * Also the `sampled` decision will be inherited.
     *
     * @deprecated Use `startSpan()`, `startSpanManual()` or `startInactiveSpan()` instead.
     */
  }, {
    key: "startChild",
    value: function startChild(spanContext) {
      var childSpan = new Span(_objectSpread(_objectSpread({}, spanContext), {}, {
        parentSpanId: this._spanId,
        sampled: this._sampled,
        traceId: this._traceId
      }));

      // eslint-disable-next-line deprecation/deprecation
      childSpan.spanRecorder = this.spanRecorder;
      // eslint-disable-next-line deprecation/deprecation
      if (childSpan.spanRecorder) {
        // eslint-disable-next-line deprecation/deprecation
        childSpan.spanRecorder.add(childSpan);
      }
      var rootSpan = getRootSpan(this);
      // TODO: still set span.transaction here until we have a more permanent solution
      // Probably similarly to the weakmap we hold in node-experimental
      // eslint-disable-next-line deprecation/deprecation
      childSpan.transaction = rootSpan;
      if (DEBUG_BUILD && rootSpan) {
        var opStr = spanContext && spanContext.op || '< unknown op >';
        var nameStr = spanToJSON(childSpan).description || '< unknown name >';
        var idStr = rootSpan.spanContext().spanId;
        var logMessage = "[Tracing] Starting '".concat(opStr, "' span on transaction '").concat(nameStr, "' (").concat(idStr, ").");
        logger.log(logMessage);
        this._logMessage = logMessage;
      }
      return childSpan;
    }

    /**
     * Sets the tag attribute on the current span.
     *
     * Can also be used to unset a tag, by passing `undefined`.
     *
     * @param key Tag key
     * @param value Tag value
     * @deprecated Use `setAttribute()` instead.
     */
  }, {
    key: "setTag",
    value: function setTag(key, value) {
      // eslint-disable-next-line deprecation/deprecation
      this.tags = _objectSpread(_objectSpread({}, this.tags), {}, _defineProperty({}, key, value));
      return this;
    }

    /**
     * Sets the data attribute on the current span
     * @param key Data key
     * @param value Data value
     * @deprecated Use `setAttribute()` instead.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {
    key: "setData",
    value: function setData(key, value) {
      // eslint-disable-next-line deprecation/deprecation
      this.data = _objectSpread(_objectSpread({}, this.data), {}, _defineProperty({}, key, value));
      return this;
    }

    /** @inheritdoc */
  }, {
    key: "setAttribute",
    value: function setAttribute(key, value) {
      if (value === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this._attributes[key];
      } else {
        this._attributes[key] = value;
      }
    }

    /** @inheritdoc */
  }, {
    key: "setAttributes",
    value: function setAttributes(attributes) {
      var _this = this;
      Object.keys(attributes).forEach(function (key) {
        return _this.setAttribute(key, attributes[key]);
      });
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "setStatus",
    value: function setStatus(value) {
      this._status = value;
      return this;
    }

    /**
     * @inheritDoc
     * @deprecated Use top-level `setHttpStatus()` instead.
     */
  }, {
    key: "setHttpStatus",
    value: function setHttpStatus(httpStatus) {
      _setHttpStatus(this, httpStatus);
      return this;
    }

    /**
     * @inheritdoc
     *
     * @deprecated Use `.updateName()` instead.
     */
  }, {
    key: "setName",
    value: function setName(name) {
      this.updateName(name);
    }

    /**
     * @inheritDoc
     */
  }, {
    key: "updateName",
    value: function updateName(name) {
      this._name = name;
      return this;
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `spanToJSON(span).status === 'ok'` instead.
     */
  }, {
    key: "isSuccess",
    value: function isSuccess() {
      return this._status === 'ok';
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `.end()` instead.
     */
  }, {
    key: "finish",
    value: function finish(endTimestamp) {
      return this.end(endTimestamp);
    }

    /** @inheritdoc */
  }, {
    key: "end",
    value: function end(endTimestamp) {
      // If already ended, skip
      if (this._endTime) {
        return;
      }
      var rootSpan = getRootSpan(this);
      if (DEBUG_BUILD &&
      // Don't call this for transactions
      rootSpan && rootSpan.spanContext().spanId !== this._spanId) {
        var logMessage = this._logMessage;
        if (logMessage) {
          logger.log(logMessage.replace('Starting', 'Finishing'));
        }
      }
      this._endTime = spanTimeInputToSeconds(endTimestamp);
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `spanToTraceHeader()` instead.
     */
  }, {
    key: "toTraceparent",
    value: function toTraceparent() {
      return spanToTraceHeader(this);
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `spanToJSON()` or access the fields directly instead.
     */
  }, {
    key: "toContext",
    value: function toContext() {
      return dropUndefinedKeys({
        data: this._getData(),
        description: this._name,
        endTimestamp: this._endTime,
        // eslint-disable-next-line deprecation/deprecation
        op: this.op,
        parentSpanId: this._parentSpanId,
        sampled: this._sampled,
        spanId: this._spanId,
        startTimestamp: this._startTime,
        status: this._status,
        // eslint-disable-next-line deprecation/deprecation
        tags: this.tags,
        traceId: this._traceId
      });
    }

    /**
     * @inheritDoc
     *
     * @deprecated Update the fields directly instead.
     */
  }, {
    key: "updateWithContext",
    value: function updateWithContext(spanContext) {
      // eslint-disable-next-line deprecation/deprecation
      this.data = spanContext.data || {};
      // eslint-disable-next-line deprecation/deprecation
      this._name = spanContext.name || spanContext.description;
      this._endTime = spanContext.endTimestamp;
      // eslint-disable-next-line deprecation/deprecation
      this.op = spanContext.op;
      this._parentSpanId = spanContext.parentSpanId;
      this._sampled = spanContext.sampled;
      this._spanId = spanContext.spanId || this._spanId;
      this._startTime = spanContext.startTimestamp || this._startTime;
      this._status = spanContext.status;
      // eslint-disable-next-line deprecation/deprecation
      this.tags = spanContext.tags || {};
      this._traceId = spanContext.traceId || this._traceId;
      return this;
    }

    /**
     * @inheritDoc
     *
     * @deprecated Use `spanToTraceContext()` util function instead.
     */
  }, {
    key: "getTraceContext",
    value: function getTraceContext() {
      return spanToTraceContext(this);
    }

    /**
     * Get JSON representation of this span.
     *
     * @hidden
     * @internal This method is purely for internal purposes and should not be used outside
     * of SDK code. If you need to get a JSON representation of a span,
     * use `spanToJSON(span)` instead.
     */
  }, {
    key: "getSpanJSON",
    value: function getSpanJSON() {
      return dropUndefinedKeys({
        data: this._getData(),
        description: this._name,
        op: this._attributes[SEMANTIC_ATTRIBUTE_SENTRY_OP],
        parent_span_id: this._parentSpanId,
        span_id: this._spanId,
        start_timestamp: this._startTime,
        status: this._status,
        // eslint-disable-next-line deprecation/deprecation
        tags: Object.keys(this.tags).length > 0 ? this.tags : undefined,
        timestamp: this._endTime,
        trace_id: this._traceId,
        origin: this._attributes[SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN],
        _metrics_summary: getMetricSummaryJsonForSpan(this),
        profile_id: this._attributes[SEMANTIC_ATTRIBUTE_PROFILE_ID],
        exclusive_time: this._exclusiveTime,
        measurements: Object.keys(this._measurements).length > 0 ? this._measurements : undefined
      });
    }

    /** @inheritdoc */
  }, {
    key: "isRecording",
    value: function isRecording() {
      return !this._endTime && !!this._sampled;
    }

    /**
     * Convert the object to JSON.
     * @deprecated Use `spanToJSON(span)` instead.
     */
  }, {
    key: "toJSON",
    value: function toJSON() {
      return this.getSpanJSON();
    }

    /**
     * Get the merged data for this span.
     * For now, this combines `data` and `attributes` together,
     * until eventually we can ingest `attributes` directly.
     */
  }, {
    key: "_getData",
    value: function _getData() {
      // eslint-disable-next-line deprecation/deprecation
      var data = this.data,
        attributes = this._attributes;
      var hasData = Object.keys(data).length > 0;
      var hasAttributes = Object.keys(attributes).length > 0;
      if (!hasData && !hasAttributes) {
        return undefined;
      }
      if (hasData && hasAttributes) {
        return _objectSpread(_objectSpread({}, data), attributes);
      }
      return hasData ? data : attributes;
    }
  }]);
  return Span;
}();
export { Span, SpanRecorder };